import {createContext, useContext} from "react";
import {useTranslationsQuery} from "./queries.generated";
import {Settings as LuxonSettings} from "luxon";
import {IntlProvider} from "react-intl";
import {bootstrapClient} from "@/apollo-client/bootstrap";
import {useWatchConcertoStorage} from "@/utils/localStorage";

function browserLang(): string {
    return navigator.language;
}

export const useLocale = () => {
    const localLocale = useWatchConcertoStorage((state) => state.profile?.locale);
    return localLocale ?? browserLang();
};

export class Translations {
    private translations: Record<string, string>;

    constructor(messages: Record<string, string>) {
        this.translations = messages;
    }

    hasMessages(): boolean {
        return this.translations != null;
    }

    get(id: string) {
        if (this.translations === null) return id;
        return this.translations[id] || id;
    }
}

const TranslationsContext = createContext(undefined);

export function useTranslations(): Translations {
    return useContext(TranslationsContext);
}

export function TranslationProvider({children}: {children: React.ReactNode}) {
    // requesting translations based on the profile - when the profile updates the page is reloaded so the new
    // transltions will be downloaded then
    const locale = useLocale();
    const {data, loading, error} = useTranslationsQuery({
        variables: {locale},
        client: bootstrapClient,
    });
    LuxonSettings.defaultLocale = locale;

    const translations = loading || error ? null : JSON.parse(data.translations);
    return (
        <IntlProvider locale={locale} messages={translations} fallbackOnEmptyString={false}>
            <TranslationsContext.Provider value={new Translations(translations)}>
                {children}
            </TranslationsContext.Provider>
        </IntlProvider>
    );
}
